import { Check, ChevronDown, Palette } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { COLOR_THEME_OPTIONS } from '@/features/settings/color-themes'
import { useSettingsStore } from '@/features/settings/stores/settings-store'
import { cn } from '@/shared/ui/cn'

interface ColorThemeToggleProps {
  className?: string
}

export function ColorThemeToggle({ className }: ColorThemeToggleProps) {
  const [open, setOpen] = useState(false)
  const colorTheme = useSettingsStore((state) => state.colorTheme)
  const setSetting = useSettingsStore((state) => state.setSetting)

  const currentThemeIndex = COLOR_THEME_OPTIONS.findIndex((option) => option.value === colorTheme)
  const resolvedCurrentTheme =
    COLOR_THEME_OPTIONS[currentThemeIndex >= 0 ? currentThemeIndex : 0] ?? COLOR_THEME_OPTIONS[0]

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={className ?? 'h-8 gap-1.5 rounded-full px-3.5'}
          aria-label={`打开配色选择，当前为${resolvedCurrentTheme?.label ?? '琥珀'}`}
        >
          <Palette className="h-4 w-4" />
          {resolvedCurrentTheme?.label ?? '琥珀'}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-auto min-w-[18rem] max-w-[22rem] rounded-2xl border-border/70 bg-popover/96 p-2.5 shadow-2xl backdrop-blur-sm"
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {COLOR_THEME_OPTIONS.map((option) => {
              const active = option.value === colorTheme

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSetting('colorTheme', option.value)
                    setOpen(false)
                  }}
                  className={cn(
                    'group relative flex h-7 w-7 items-center justify-center rounded-full transition-all',
                    'hover:scale-105 focus-visible:scale-105',
                    active
                      ? 'ring-2 ring-primary ring-offset-2 ring-offset-popover'
                      : 'ring-1 ring-white/12 hover:ring-white/25',
                  )}
                  style={{
                    background: `linear-gradient(135deg, ${option.swatch} 0%, ${option.glow} 100%)`,
                  }}
                  aria-label={`切换到${option.label}配色`}
                  aria-pressed={active}
                  title={`${option.label} · ${option.description}`}
                >
                  {active && <Check className="h-3.5 w-3.5 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" />}
                </button>
              )
            })}
          </div>

          <div className="h-10 w-px bg-border/70" />

          <div className="min-w-[3.75rem] shrink-0">
            <p className="whitespace-nowrap text-xs font-semibold tracking-wide text-foreground">
              主题色
            </p>
            <p className="mt-0.5 whitespace-nowrap text-xs text-muted-foreground">
              {resolvedCurrentTheme.label}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}